import { InjectionToken } from '@angular/core';
import {
  CardType,
  ConnectCardBadgeRequest,
  ConnectedBadgeDetails,
  ConnectedBadgeStatus,
  EditorContent,
  Resource,
} from '@app/app/shared/models';
import { ObservableResult } from '@app/app/shared/store';

export interface CardSectionUpdateRequest {
  uid?: string;
  dynamicContent: EditorContent[];
  languageCode: string;
  cardHeadline?: string;
  title?: string;
  layoutType?: string;
}

export interface CreatedCardInfo {
  cardUid: string;
  cardUri: string;
  sectionUid: string;
}

export interface CreatedEventInfo {
  eventUid: string;
  eventUri: string;
}

export interface CreatedAssessmentInfo {
  assessmentUid: string;
  assessmentUri: string;
}

export interface CreateIndependentCardRequest {
  title?: string;
  start?: string;
  end?: string;
  timeZone?: string;
}

export interface CardsDataService {
  createEmptyCard(playlistUid: string, sectionUid: string): ObservableResult<CreatedCardInfo>;

  createEmptyEvent(playlistUid: string, sectionUid: string): ObservableResult<CreatedEventInfo>;

  /** With `is360`, the backend seeds the assessment with feedback provider settings (a 360 assessment). */
  createEmptyAssessment(playlistUid: string, sectionUid: string, is360: boolean): ObservableResult<CreatedAssessmentInfo>;

  createIndependentCard(type: CardType, request?: CreateIndependentCardRequest): ObservableResult<Resource>;

  updateCardSection(cardUid: string, sectionUid: string, request: CardSectionUpdateRequest): ObservableResult<void>;

  updateCardHeadline(cardUid: string, headline: string): ObservableResult<void>;

  /** With `includeProgress`, the v3 endpoint enriches the card with the learner progress (incl. review notes) as `progress`. */
  getCard(cardUri: string, playlistPath?: string, includeProgress?: boolean): ObservableResult<Resource>;

  /**
   * Fetches a card by its internal id rather than its uri slug. `GET /cards/{uri}` resolves a slug, so it
   * cannot load a card when only the id is known (e.g. an assignment card, by its assignment id).
   * Backed by the dedicated `GET /cards/by-id/{cardId}` endpoint.
   */
  getCardById(cardId: string): ObservableResult<Resource>;

  /**
   * Connects a badge to the card, so completing the card awards it. Replaces any existing connection —
   * changing the badge is a single call, not disconnect-then-connect. The card must not be an
   * ASSIGNMENT (their completion is tracked by the gradebook, so the badge would never be issued).
   */
  connectCardBadge(cardUid: string, request: ConnectCardBadgeRequest): ObservableResult<ConnectedBadgeDetails>;

  /**
   * Activates or deactivates the connection while keeping the badge attached, so the author's choice
   * survives being switched off. Fails when the card has no connected badge — connect first.
   */
  updateCardBadgeStatus(cardUid: string, status: ConnectedBadgeStatus): ObservableResult<ConnectedBadgeDetails>;

  /**
   * Removes the connection. The badge itself is kept (it stays available for other cards) and badges
   * already issued to learners are not revoked. Idempotent, so a double click is harmless.
   */
  disconnectCardBadge(cardUid: string): ObservableResult<void>;

  archiveCard(cardUid: string): ObservableResult<void>;

  discardCard(cardUid: string): ObservableResult<void>;

  publishCard(cardUid: string): ObservableResult<void>;

  unpublishCard(cardUid: string): ObservableResult<void>;
}

export const CARDS_DATA_SERVICE = new InjectionToken<CardsDataService>('CARDS_DATA_SERVICE');
